import { useState } from "react";
import { cn } from "@/lib/cn";

/**
 * Workshop: Notifications section for Settings.
 * Toggle groups for email and in-app notification preferences.
 */

interface NotificationToggle {
  id: string;
  label: string;
  defaultEnabled: boolean;
  testId: string;
  toggleTestId: string;
}

const EMAIL_TOGGLES: NotificationToggle[] = [
  {
    id: "weekly-digest",
    label: "Weekly digest",
    defaultEnabled: true,
    testId: "notif-weekly-digest",
    toggleTestId: "notif-toggle-weekly-digest",
  },
  {
    id: "agent-completed-task",
    label: "Agent completed task",
    defaultEnabled: true,
    testId: "notif-agent-completed-task",
    toggleTestId: "notif-toggle-agent-completed-task",
  },
  {
    id: "collaborator-activity",
    label: "Collaborator activity",
    defaultEnabled: false,
    testId: "notif-collaborator-activity",
    toggleTestId: "notif-toggle-collaborator-activity",
  },
];

const IN_APP_TOGGLES: NotificationToggle[] = [
  {
    id: "mentions",
    label: "Mentions",
    defaultEnabled: true,
    testId: "notif-mentions",
    toggleTestId: "notif-toggle-mentions",
  },
  {
    id: "agent-updates",
    label: "Agent updates",
    defaultEnabled: true,
    testId: "notif-agent-updates",
    toggleTestId: "notif-toggle-agent-updates",
  },
  {
    id: "project-invites",
    label: "Project invites",
    defaultEnabled: true,
    testId: "notif-project-invites",
    toggleTestId: "notif-toggle-project-invites",
  },
];

function buildInitialState(
  toggles: NotificationToggle[],
): Record<string, boolean> {
  const state: Record<string, boolean> = {};
  for (const t of toggles) {
    state[t.id] = t.defaultEnabled;
  }
  return state;
}

interface ToggleSwitchProps {
  enabled: boolean;
  onToggle: () => void;
  "data-testid": string;
}

function ToggleSwitch({
  enabled,
  onToggle,
  "data-testid": testId,
}: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      className={cn(
        "relative h-5 w-9 shrink-0 rounded-full transition-colors",
        enabled
          ? "bg-accent"
          : "border border-glass-stroke-light bg-glass-fill-heavy",
      )}
      onClick={onToggle}
      data-testid={testId}
    >
      <span
        className={cn(
          "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all",
          enabled ? "left-[18px]" : "left-0.5",
        )}
      />
    </button>
  );
}

interface NotificationsSectionProps {
  className?: string;
  "data-testid"?: string;
}

export function NotificationsSection({
  className,
  "data-testid": testId = "settings-notifications",
}: NotificationsSectionProps) {
  const [emailState, setEmailState] = useState<Record<string, boolean>>(
    buildInitialState(EMAIL_TOGGLES),
  );
  const [inAppState, setInAppState] = useState<Record<string, boolean>>(
    buildInitialState(IN_APP_TOGGLES),
  );

  function toggleEmail(id: string) {
    setEmailState((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleInApp(id: string) {
    setInAppState((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <div className={cn("flex flex-col gap-4", className)} data-testid={testId}>
      {/* Email Notifications */}
      <div>
        <p className="mb-2 text-[10px] font-medium text-secondary">
          EMAIL NOTIFICATIONS
        </p>
        <div className="flex flex-col gap-1">
          {EMAIL_TOGGLES.map((toggle) => (
            <div
              key={toggle.id}
              className="flex items-center justify-between rounded-lg px-3 py-2.5"
              data-testid={toggle.testId}
            >
              <span className="text-[11px] text-primary">{toggle.label}</span>
              <ToggleSwitch
                enabled={emailState[toggle.id] ?? false}
                onToggle={() => toggleEmail(toggle.id)}
                data-testid={toggle.toggleTestId}
              />
            </div>
          ))}
        </div>
      </div>

      {/* In-App Notifications */}
      <div>
        <p className="mb-2 text-[10px] font-medium text-secondary">
          IN-APP NOTIFICATIONS
        </p>
        <div className="flex flex-col gap-1">
          {IN_APP_TOGGLES.map((toggle) => (
            <div
              key={toggle.id}
              className="flex items-center justify-between rounded-lg px-3 py-2.5"
              data-testid={toggle.testId}
            >
              <span className="text-[11px] text-primary">{toggle.label}</span>
              <ToggleSwitch
                enabled={inAppState[toggle.id] ?? false}
                onToggle={() => toggleInApp(toggle.id)}
                data-testid={toggle.toggleTestId}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Footer note */}
      <p className="text-[10px] text-secondary">
        Per-project notification overrides are available in the Projects
        section.
      </p>
    </div>
  );
}

export type { NotificationsSectionProps };
